import type {
  BreathSegment,
  FachKey,
  Metric,
  MetricKey,
  PitchRegister,
  PitchSeries,
  RangeInfo,
  Report,
  Vibrato,
} from '@muzly/voice-engine';
import { measuredMetrics, vibratoAxisScore } from '@muzly/voice-engine';

import { SURVEY_STEPS, otherKey } from '../survey';

import type { CaptureSource, FrameStats } from '../capture';
import type { OnboardingAnswer } from '../flow';
import type { SongScore } from '../songScoring';

/**
 * toDiagnosisRecord — 엔진 `Report` → 온보딩이 **저장하는 것**으로의 투영.
 *
 * 왜 `Report`를 그대로 안 쌓나(티켓 04): 온디바이스 캡처는 스펙트럼을 못 넘기므로
 * `character`·`registers`·`placement`·`vocalAge.voiceAge`·`resonance`·`singersFormant`·
 * `f3`/`f4`가 전부 상수이거나 미측정이다. 그대로 저장하면 이력에 죽은 값이 쌓이고,
 * 나중에 "그때는 이랬다"로 읽힌다.
 *
 * `Report` 타입에서 **지우는 것으로 해결하면 안 된다** — landing은 WebAudio FFT로,
 * admin은 엔진 재실행으로 그 필드들을 실제로 계산한다. 거기선 죽은 값이 아니다.
 * 좁은 타입을 저장 계층에 따로 두는 게 두 앱을 안 깨는 최소 변경이다.
 *
 * React·저장소 IO 의존 없음 — vitest 대상.
 */

/**
 * 스키마가 바뀌면 올린다. 읽는 쪽은 모르는 버전을 **버린다**(마이그레이션 없음).
 *
 * v2: 설문이 덱 순서로 재편되고(닉네임 추가, 측정환경 2지→3지), 네이티브가 실어 보내는
 * 파생 스칼라(`hnrDb`·`resonanceRatio`)가 붙었다. v1 회차는 폐기된다.
 *
 * 슬라이스 12가 더한 `songScore`·`registerBreaks`, ABI v4가 더한 공명 스칼라 넷
 * (`resonanceLevelDb`·`resonanceCenterHz`·`brightnessHz`·`bandwidthHz`)은 **버전을 안
 * 올린다**: 전부 optional이라 없는 채로 저장된 v2 레코드가 그대로 읽히고(필드가 없다 =
 * 그때는 못 쟀다), 폐기할 이유가 없다. 버전을 올리면 이미 쌓인 v2 회차만 이유 없이 지워진다.
 */
export const DIAGNOSIS_SCHEMA_VERSION = 2;

/**
 * 온디바이스에서 **점수로** 나오는 축. `resonance`·`singersFormant`는 스펙트럼이 있어야
 * 나오는데 프레임에 없다(GOAL.md: raw PCM 금지) → 점수로는 저장하지 않는다.
 *
 * 네이티브가 새로 재는 HNR·울림을 여기 축으로 더하지 않는 이유는 아래 `hnrDb` 주석 참고 —
 * 원값으로 담는다.
 */
export const STORED_METRIC_KEYS: MetricKey[] = [
  'pitchAccuracy',
  'pitchStability',
  'sustain',
  'vibrato',
];

/**
 * 리포트 축 → 저장할 점수. 저장 축이면서 **이번에 잰** 것만 남는다.
 *
 * `vibrato`는 저장 축이지만 회차마다 못 잴 수 있다 — 이어진 유성 구간이 셀 주기를 못
 * 채우면 엔진이 `measured: false`와 자리 채움 `val`을 낸다. 그걸 담으면 탭이 0점을 말하고
 * 균등 평균(`overallScore`)이 깎인다. 결과 화면과 서버 메인 `score`(`segmentScore.ts`)가
 * 같은 걸음을 밟아야 하므로 걸러내기는 여기 한 곳이다.
 */
export const storedScores = (
  metrics: Metric[],
): { key: MetricKey; val: number }[] =>
  measuredMetrics(metrics)
    .filter((m) => STORED_METRIC_KEYS.includes(m.key))
    .map((m) => ({ key: m.key, val: m.val }));

export interface DiagnosisRecord {
  schemaVersion: number;
  id: string;
  /** epoch ms (호출자가 주입 — 테스트 결정성). */
  completedAt: number;
  /** 실측(native)인지 브라우저 mock인지. mock은 저장하지 않는다(diagnosisStore). */
  source: CaptureSource;
  /** 측정된 축만, 엔진이 낸 순서대로. */
  scores: { key: MetricKey; val: number }[];
  /** 최대발성지속시간(초). `scores`의 sustain은 점수, 이건 원값. */
  sustainSec: number;
  /**
   * 배음 대비 잡음비 평균(dB) — 네이티브가 프레임에 실어 보낸 **원값**(맑기의 근거).
   *
   * 점수로 안 바꾸는 이유가 둘이다. ① 눈금이 아직 없다: 몇 dB가 몇 점인지는 보정 전이고,
   * 지금 임의 눈금으로 굳히면 눈금이 바뀌는 날 이력 전체가 거짓이 된다. `sustainSec`을
   * 점수 옆에 원값으로 둔 것과 같은 이유다. ② 엔진 축으로 못 올린다: `Clip.hnrs`는
   * landing에서 스펙트럼 프록시(0..10 무차원, spectral.ts), 네이티브 경로에서 실제 dB라
   * 같은 산식이 두 앱에서 다른 것을 잰다.
   *
   * **없으면 필드가 없다.** 구 네이티브 바이너리는 이 값을 안 실어 보내는데, 0으로 채우면
   * 이력이 "그때는 맑기 0이었다"로 읽힌다.
   */
  hnrDb?: number;
  /**
   * 고역 대역 에너지비 평균(0..1) — 울림의 근거. 엔진의 `resonance` 축으로 승격하지
   * 않는다: 그건 HNR + 스펙트럼 중심 변동으로 만든 값이라 대역 에너지비와 다른 물리량이고,
   * 같은 키가 앱마다 다른 것을 재면 엔진을 검증하는 admin이 무의미해진다.
   * `hnrDb`와 같은 "없으면 없다" 규약.
   */
  resonanceRatio?: number;
  /**
   * 공명 세기 평균(dB) — 위 `resonanceRatio`와 **같은 것을 다른 자로 잰 값**이다.
   * 비율은 0..1이라 작은 쪽이 뭉치고, dB는 화면 눈금(§8.8 `-20 ~ -10dB`)이 쓰는 자다.
   * 여기서 비율로부터 계산하지 않는다: 바닥값(-40dB)은 네이티브가 소유한 상수라
   * 베껴 오면 그날부터 두 벌이 된다(framesToClips 주석).
   */
  resonanceLevelDb?: number;
  /**
   * 울림 대역(1.5~5kHz) 안에서 에너지가 모인 자리(Hz) — 시안 §8.8의 `공명 중심 주파수`.
   * 대역 경계는 네이티브가 소유한다.
   */
  resonanceCenterHz?: number;
  /** 스펙트럼 전체의 무게중심(Hz) — 시안 §8.9의 `목소리 밝기` 축. */
  brightnessHz?: number;
  /** 무게중심 둘레의 퍼짐(Hz) — 시안 §8.9의 `울림의 넓이` 축. 좁을수록 집중된 소리다. */
  bandwidthHz?: number;
  /**
   * 떨림의 속도(Hz)·폭(cents)·규칙성(%) — f0 시계열에서 잰 **원값**.
   *
   * `scores`의 `vibrato` 축은 **이 원값에서 나온다**(`vibratoAxisScore` — 속도·폭이 화면의
   * 참고 구간에 얼마나 붙어 있나). 둘은 같은 자로 잰 값이라, 폭이 참고 구간을 한참 벗어났는데
   * 점수가 만점인 일은 없다(TestFlight #70). 원값이 없으면 축도 없다.
   *
   * `hnrDb`와 같은 "없으면 필드가 없다" 규약. 이어진 유성 구간이 셀 주기를 못 채운
   * 회차는 필드가 빠지고, 화면은 0이 아니라 "측정 준비 중"을 그린다.
   */
  vibrato?: Vibrato;
  /**
   * 지속음의 시작·중간·후반 흔들림 점수 — `scores`의 `pitchStability`와 **같은 자로 잰**
   * 값이라 나란히 읽힌다. 한 숫자로는 "처음엔 흔들리다 잡힌 사람"과 "잘 시작했다가 숨이
   * 떨어진 사람"을 못 가르는데, 그 둘은 반대 조언이 필요하다.
   *
   * 테이크가 닿지 않은 구간은 **배열에 없다**. 5초에 끝난 녹음의 `후반`을 0점으로 적으면
   * 화면이 "끝에서 무너졌다"고 말한다.
   */
  breath?: BreathSegment[];
  /**
   * 지속음의 음정 곡선 — 그 회차 중앙값 기준 정수 cents, 일정 간격(`PitchSeries`). 호흡·떨림
   * 차트가 모식도 대신 이걸 그린다(TestFlight #56·#57).
   *
   * **이 기기에서 끝낸 회차에만 있다.** 서버로는 안 보내므로 서버에서 복원한 회차·이 필드
   * 이전 회차는 없고, 차트는 예전 그림으로 돌아간다(plan Decisions "시계열 저장 위치").
   */
  pitchSeries?: PitchSeries;
  /**
   * 음역을 다섯 구간으로 나눠 잰 구간별 안정성 — 그 회차에 **실제로 낸 모든 음**이 근거다.
   *
   * 경계가 회차마다 다른 게 요점이다: 다섯 구간은 그 사람의 음역을 비율로 나눈 것이라
   * 베이스와 소프라노의 `전환`이 같은 음일 리 없다. 그래서 경계(`lowMidi`·`highMidi`)도
   * 값과 함께 저장한다 — 화면이 나중에 다시 계산하면 그때의 음역으로 옛 회차를 그린다.
   *
   * 표본이 모자란 구간은 `score`가 없다(줄은 남는다 — 화면이 라벨을 그려야 한다).
   */
  pitchRegisters?: PitchRegister[];
  /**
   * 노래 따라부르기 채점 — **그 진단을 실제로 채점했을 때만** 있다. 건너뛰었거나 음원을
   * 못 받았거나 소리가 안 잡힌 회차는 필드가 없다(0점이 아니다 — songScoring 모듈 주석).
   * 이 필드의 유무가 결과 화면 "진단 3"을 그릴지 말지를 가른다. **음정 정확도 축은 이
   * 값으로 승격되지 않는다** — 눈금이 다른 값이라 같은 축에 못 싣는다(metricGrades 주석).
   */
  songScore?: SongScore;
  /**
   * 고음 세그먼트에서 잡힌 성구 전환 시각(ms) — 길이가 곧 횟수다.
   * **`[]`(0회)와 필드 없음(측정 불가)은 다른 사실이다**(registerTransition 모듈 주석):
   * 올라간 적이 없는 시도는 "끊김이 없었다"가 아니라 답할 수 없는 질문이다.
   */
  registerBreaks?: number[];
  /** 저음·고음 테스트가 다 유효했을 때만. 점수가 아니라 사실이다. */
  range: RangeInfo | null;
  fach: FachKey | null;
  /**
   * 설문 응답(자유입력 포함). 성별·출생연도는 안 변하지만 **오늘의 컨디션·측정환경은
   * 회차마다 다르고** 그게 점수 해석의 근거다(리소스 p11). 나이는 따로 안 담는다 —
   * `birthYear`에서 파생되는 값이라 여기 굳히면 "현재 연도"가 저장 시점에 박힌다.
   */
  survey: Record<string, OnboardingAnswer>;
}

/** 설문 단계의 응답만 골라낸다 — `SURVEY_STEPS`가 늘면 자동으로 따라온다. */
const surveyAnswers = (
  answers: Record<string, OnboardingAnswer>,
): Record<string, OnboardingAnswer> => {
  const picked: Record<string, OnboardingAnswer> = {};
  for (const step of SURVEY_STEPS) {
    for (const key of [step.id, otherKey(step.id)]) {
      if (answers[key] !== undefined) {
        picked[key] = answers[key];
      }
    }
  }
  return picked;
};

export const toDiagnosisRecord = (input: {
  report: Report;
  answers: Record<string, OnboardingAnswer>;
  source: CaptureSource;
  /** `/a/` 길게에서 모은 파생 스칼라. 안 잰 값은 필드가 없다(0 아님). */
  stats: FrameStats;
  /** 노래를 채점했을 때만. 없으면 필드가 안 실린다. */
  songScore?: SongScore;
  /** 고음 세그먼트 프레임에서 센 성구 전환. `[]`=0회, 생략=측정 불가. */
  registerBreaks?: number[];
  /** 음역 5구간 안정성. 음역을 못 쟀으면 없다. */
  pitchRegisters?: PitchRegister[];
  completedAt: number;
}): DiagnosisRecord => ({
  schemaVersion: DIAGNOSIS_SCHEMA_VERSION,
  id: `d${input.completedAt}`,
  completedAt: input.completedAt,
  source: input.source,
  scores: storedScores(input.report.metrics),
  sustainSec: input.report.sustainSec,
  // undefined 필드는 JSON에서 통째로 빠진다 — "안 쟀다"가 그대로 남는다.
  ...input.stats,
  ...(input.songScore ? { songScore: input.songScore } : {}),
  ...(input.registerBreaks ? { registerBreaks: input.registerBreaks } : {}),
  ...(input.pitchRegisters?.length
    ? { pitchRegisters: input.pitchRegisters }
    : {}),
  range: input.report.range,
  fach: input.report.fach,
  survey: surveyAnswers(input.answers),
});

/**
 * 저장된 회차의 `vibrato` 점수를 **원값에서 다시 낸다**(읽을 때마다, 멱등).
 *
 * 이 산식 이전에 쌓인 회차(로컬·서버 복원 모두 `schemaVersion` 2 그대로)는 옛 프록시 점수를
 * 들고 있다 — 반음 기준 ±50에 접힌 흔들림을 본 값이라 ±271 cents 회차도 100이다(#70).
 * 점수만 **그 회차가 보여 주는 원값에서** 다시 낸다 — 원값 자체는 다시 재지 않는다(프레임이
 * 없다). 옥타브 되접기 전에 잰 원값은 오검출로 부풀었을 수 있지만, 그래도 탭 점수와 그 아래
 * 속도·폭은 같은 수를 말한다. 원값이 없는 회차는 비브라토를 못 잰 것이므로 축을 뺀다
 * (`storedScores`와 같은 규칙).
 */
export const rescoreVibrato = (record: DiagnosisRecord): DiagnosisRecord => {
  const others = record.scores.filter((s) => s.key !== 'vibrato');
  const scores = record.vibrato
    ? [
        ...others,
        {
          key: 'vibrato' as const,
          val: Math.round(vibratoAxisScore(record.vibrato)),
        },
      ]
    : others;
  return { ...record, scores };
};

/**
 * 회차의 **종합 점수** — 저장된 축의 균등 평균(미보정: 가중치는 아직 없다 — 플랜 What's NOT).
 * 축이 하나도 없으면 null이다(0이 아니다).
 *
 * 산식이 여기 있는 이유: 홈(`features/home/recentSummary`)과 결과 화면이 **같은 숫자**를
 * 말해야 하는데, 두 화면이 각자 평균을 내면 같은 회차가 홈에서 64, 결과에서 65가 된다.
 * 레코드가 사는 자리에 두면 두 소비처가 같은 함수를 부른다(홈은 이미 이 모듈을 읽는다 —
 * 반대 방향 의존이 없어 순환도 안 생긴다).
 *
 * **분모는 회차마다 셋 또는 넷이다.** 음정 세 축은 지속음 클립 하나에서 항상 나오고,
 * `vibrato`는 비브라토를 잰 회차에만 있다(`storedScores`). 못 잰 축을 0으로 넣지 않고
 * 분모에서 빼는 것이 엔진의 `weightedOverall`과 같은 규칙이다.
 */
export const overallScore = (record: DiagnosisRecord): number | null => {
  if (record.scores.length === 0) {
    return null;
  }
  const sum = record.scores.reduce((total, s) => total + s.val, 0);
  return Math.round(sum / record.scores.length);
};
